import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { describeNextRun, describeTrigger, formatDateTime, runStatusTone } from "../../utils/workflowTriggers";

const Card = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border: 1px solid var(--content-item-border-color);
  background-color: var(--card-bg);
  border-radius: 8px;
  box-shadow: var(--content-item-box-shadow);
`;

const IconBox = styled.div`
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: var(--sidebar-active-item-bg);
  color: var(--main-font-color);
  font-size: 18px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--main-font-color);
`;

const TypeLabel = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #a0a6b1;
`;

const Detail = styled.div`
  font-size: 12.5px;
  color: #a0a6b1;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Mono = styled.span`
  font-family: monospace;
  font-size: 12px;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 5px;
  padding: 1px 6px;
  word-break: break-all;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => props.$tone};
  border: 1px solid ${(props) => props.$tone};
  background-color: ${(props) => `${props.$tone}1a`};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  position: relative;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
  font-size: 15px;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const SmallButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  font-size: 11.5px;
  cursor: pointer;
`;

const Menu = styled.div`
  position: absolute;
  top: 30px;
  right: 0;
  z-index: 20;
  min-width: 170px;
  display: flex;
  flex-direction: column;
  padding: 4px;
  background-color: var(--dialog-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${(props) => (props.$danger ? "#e74c3c" : "var(--main-font-color)")};
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: var(--sidebar-active-item-bg);
  }
`;

const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: #a0a6b1;
  cursor: pointer;
  user-select: none;
`;

/**
 * One trigger, as a card: type, name, status, endpoint and its actions.
 */
export const TriggerCard = ({
  trigger,
  typeMeta,
  endpointBase,
  busy = false,
  onToggle,
  onEdit,
  onRunNow,
  onDelete,
  onViewRuns,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
    };

    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [menuOpen]);

  const endpointPath = trigger.endpoint?.path ?? null;
  const endpointUrl = endpointPath ? `${endpointBase ?? ""}${endpointPath}` : null;
  const isEnabled = trigger.enabled !== false;

  const copy = async () => {
    if (!endpointUrl) return;

    try {
      await navigator.clipboard.writeText(endpointUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch (error) {
      console.log("CLIPBOARD ERROR: ", error);
    }
  };

  const typeLabel = typeMeta?.label ?? trigger.type;
  const icon = typeMeta?.icon ?? "lucide:bolt";

  return (
    <Card>
      <IconBox>
        <Icon icon={icon} />
      </IconBox>

      <Body>
        <TitleRow>
          <Name>{trigger.name || typeLabel}</Name>
          <TypeLabel>{typeLabel}</TypeLabel>
          {trigger.last_run_status ? (
            <Chip $tone={runStatusTone(trigger.last_run_status)}>{trigger.last_run_status}</Chip>
          ) : null}
          {!isEnabled ? <Chip $tone="#95a5a6">paused</Chip> : null}
        </TitleRow>

        <Detail>
          <span>{trigger.description || describeTrigger(trigger)}</span>
        </Detail>

        <Detail>
          {trigger.type === "schedule" ? <span>{describeNextRun(trigger)}</span> : null}
          {trigger.last_run_at ? (
            <span>
              Last run {formatDateTime(trigger.last_run_at)}
              {trigger.last_error ? " · failed" : ""}
            </span>
          ) : null}
          {trigger.run_count ? <span>{trigger.run_count} run{trigger.run_count === 1 ? "" : "s"}</span> : null}
        </Detail>

        {endpointUrl ? (
          <Detail>
            <Mono>{endpointUrl}</Mono>
            <SmallButton type="button" onClick={copy} title="Copy the public endpoint URL">
              <Icon icon={copied ? "lucide:check" : "lucide:copy"} />
              {copied ? "Copied" : "Copy"}
            </SmallButton>
          </Detail>
        ) : null}

        {trigger.api_key_name ? (
          <Detail>
            <Icon icon="lucide:key-round" /> Key <Mono>{trigger.api_key_name}</Mono>
          </Detail>
        ) : null}
      </Body>

      <Actions ref={menuRef}>
        <Toggle>
          <input
            type="checkbox"
            checked={isEnabled}
            disabled={busy}
            onChange={(event) => onToggle?.(trigger, event.target.checked)}
            aria-label="Enabled"
          />
          {isEnabled ? "On" : "Off"}
        </Toggle>

        <SmallButton type="button" onClick={() => onViewRuns?.(trigger)} title="View this trigger's runs">
          <Icon icon="lucide:list" /> Runs
        </SmallButton>

        <IconButton
          type="button"
          onClick={() => {
            setMenuOpen((open) => !open);
            setConfirmDelete(false);
          }}
          disabled={busy}
          title="More actions"
          aria-label="More actions"
        >
          <Icon icon="lucide:ellipsis-vertical" />
        </IconButton>

        {menuOpen ? (
          <Menu>
            <MenuItem
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onEdit?.(trigger);
              }}
            >
              <Icon icon="lucide:pencil" /> Edit
            </MenuItem>
            <MenuItem
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onRunNow?.(trigger);
              }}
            >
              <Icon icon="lucide:play" /> Run now
            </MenuItem>
            {endpointUrl ? (
              <MenuItem
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  copy();
                }}
              >
                <Icon icon="lucide:link" /> Copy endpoint
              </MenuItem>
            ) : null}
            {confirmDelete ? (
              <>
                <MenuItem
                  type="button"
                  $danger
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirmDelete(false);
                    onDelete?.(trigger);
                  }}
                >
                  <Icon icon="lucide:trash-can-outline" /> Confirm delete
                </MenuItem>
                <MenuItem type="button" onClick={() => setConfirmDelete(false)}>
                  <Icon icon="lucide:x" /> Cancel
                </MenuItem>
              </>
            ) : (
              <MenuItem type="button" $danger onClick={() => setConfirmDelete(true)}>
                <Icon icon="lucide:trash-can-outline" /> Delete
              </MenuItem>
            )}
          </Menu>
        ) : null}
      </Actions>
    </Card>
  );
};

export default TriggerCard;
